import { gql, Reference } from '@apollo/client';
import {
    isComsEnabled,
    isServerSideComsTokenEnabled,
} from '@thoughtspot/session-service';
import _ from 'lodash';
import { getCachedAnswer } from './services/answer/answer-cache-service';
import {
    ChartViz,
    DisplayMode,
    HeadlineViz,
    PhraseType,
    SagePhrase,
    TableViz,
    VizData,
} from './services/generated/graphql-types';

export enum ClientTypes {
    Blink,
    Mobile,
    FullEmbed,
    PublicApiDirect,
    BlinkV2,
}

export function hasUnresolvedTokens(phrases: readonly SagePhrase[]): boolean {
    return phrases.some(
        phrase =>
            !phrase.isComplete || phrase.type === PhraseType.UndefinedPhrase,
    );
}

export function getTotalTokens(phrases: readonly SagePhrase[]): number {
    return phrases.filter(
        phrase =>
            phrase.isComplete && phrase.type !== PhraseType.UndefinedPhrase,
    ).length;
}

export function parseVizColumnData(columnDataLite: any) {
    const newColumDataLite = columnDataLite
        ? columnDataLite.map((columnData: any) => {
              const colData = { ...columnData };
              if (typeof colData.dataValue === 'string') {
                  colData.dataValue = JSON.parse(colData.dataValue);
              }
              return colData;
          })
        : undefined;
    return newColumDataLite;
}

export const mergeVizData = () => (
    existing: Reference,
    incoming: Reference,
    props: any,
) => {
    const { readField, cache } = props;
    const answerSessionId = readField('sessionId', readField('id', incoming));
    const vizDataMap: VizData[] = readField('vizData', incoming);
    const cachedAnswer = getCachedAnswer(
        cache,
        answerSessionId,
        gql`
            {
                answer {
                    id
                    visualizations {
                        id
                    }
                }
            }
        `,
    );
    cachedAnswer.answer.visualizations.forEach(viz => {
        // eslint-disable-next-line no-underscore-dangle
        const typename = (viz as HeadlineViz | ChartViz | TableViz).__typename;
        const vizData = vizDataMap.find(map => map.vizId === viz.id);
        // TODO: chart viz params handling
        // TODO: merge use case handling when we are fetching the second page
        if (vizData) {
            cache.writeFragment({
                id: cache.identify({
                    id: viz.id,
                    __typename: typename,
                }),
                fragment: gql`
                    fragment viz on ${typename} {
                        data
                    }
                `,
                data: {
                    data: _.cloneDeep(vizData.data),
                },
            });
        }
    });
    return incoming;
};

export const getResolvedVizsColumnIds = (
    displayMode: string,
    vizs: any[],
): any[] => {
    let columnIds: any[] = [];
    if (displayMode === DisplayMode.TableMode) {
        const tableViz: any = _.find(
            vizs,
            // eslint-disable-next-line no-underscore-dangle
            ref => ref?.__typename === 'TableViz',
        );
        columnIds = tableViz?.columns?.map((c: any) => c.column.id);
    } else if (displayMode === DisplayMode.ChartMode) {
        const chartViz: any = _.find(
            vizs,
            // eslint-disable-next-line no-underscore-dangle
            ref => ref?.__typename === 'ChartViz',
        );
        columnIds = chartViz?.columns?.map((c: any) => c.column.id);
    }
    return columnIds?.length > 0 ? columnIds : [];
};

export const getCustomChartConfig = (displayMode: string, vizs: any[]) => {
    if (displayMode !== DisplayMode.ChartMode) {
        return null;
    }
    const chartViz: any = _.find(
        vizs,
        // eslint-disable-next-line no-underscore-dangle
        ref => ref?.__typename === 'ChartViz',
    );
    return chartViz?.config?.customChartConfig;
};

export const operationsRequiringComsToken: readonly string[] = [
    'GetCommunicationPreferences',
    'UpdateCommunicationPreferences',
    'GetMessages',
    'GetMessagesMetadata',
    'UpdateCommunicationMessage',
    'UpdateCommunicationMessages',
    'GetCommunicationConnections',
    'BuildCommunicationConnection',
    'DeleteCommunicationConnection',
    'GetChannels',
];

export function shouldFetchComsToken(operationName: string): boolean {
    if (!isComsEnabled()) {
        return false;
    }
    if (!operationsRequiringComsToken.includes(operationName)) {
        return false;
    }
    // When server-side token fetching is enabled via ENABLE_SERVER_SIDE_COMS_TOKEN,
    // the server middleware (prism/src/server/coms-token-middleware.ts) adds the token
    // to request headers. Client-side fetching is unnecessary and would be redundant.
    if (isServerSideComsTokenEnabled()) {
        return false;
    }
    return true;
}
